/**
 * El stylesheet que se inyecta al arrancar la app.
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9). Sigue siendo
 * un template string; el paso 05 lo reemplaza por `emitVars()` generado.
 */

import {
  brand,
  surface,
  ink,
  line,
  semantic,
  tagExtras,
  emotion,
  kind,
  node,
  fill,
  elevation,
  typography,
  radius,
  space,
  control,
  tagPalette,
} from './primitives';
import { theme, palette } from './palette';
import { emitAssetVars, siliaAssets } from './brands/assets';

/* ═══════════════════════════════════════════════════════════════════════
   6. CSS VARIABLES
   ═══════════════════════════════════════════════════════════════════════ */

/** Tag palette vars, in both shapes — see the note on `tagPalette`. */
const tagVars = tagPalette
  .map(
    (c) =>
      `    --silia-tag-${c.key}-bg: ${c.fill};\n` +
      `    --silia-tag-${c.key}-tag: ${c.tag};\n` +
      `    --silia-tag-${c.key}-dot: ${c.dot};\n` +
      `    --silia-tag-${c.key}-text: ${c.text};`,
  )
  .join('\n');

/** `--ant-*` aliases for the tag palette, consumed by Data Views CSS today. */
const tagVarsLegacy = tagPalette
  .map(
    (c) =>
      `    --ant-tag-${c.key}-fill: ${c.fill};\n` +
      `    --ant-tag-${c.key}-tag: ${c.tag};\n` +
      `    --ant-tag-${c.key}-dot: ${c.dot};\n` +
      `    --ant-tag-${c.key}-text: ${c.text};`,
  )
  .join('\n');

/**
 * The legacy `--ant-*` colour aliases, each one resolved through the
 * `--silia-*` token of the same role (SL-1918).
 *
 * `token` is a `palette` key, so the value is a `var(--silia-*)` and follows the
 * active brand. The mapping is written out one alias at a time on purpose: two
 * roles can share a hex (`text-inverse` and `bg-container` are both #ffffff), so
 * deriving it by matching values would merge roles — the bug that retired
 * `toTraxion()`.
 *
 * `was` is the `theme` key the alias used to emit as a literal. It is the
 * oracle `legacyColorAliases.test.ts` checks against, so the default brand is
 * proven unchanged. It defaults to `alias`; only the three renamed text
 * aliases spell it out.
 *
 * Migration scaffolding, exported only for that test: delete a row when the
 * last CSS reading its alias moves to `--silia-*`, and the table with the last
 * row.
 *
 * JS must not read these from `document.documentElement`: the brand class
 * lives on `<body>`, so `<html>` answers Silia under any brand. Code that needs
 * a real colour reads the `--silia-*` token with `brandColor()` (SL-1924).
 */
export const LEGACY_COLOR_ALIASES: ReadonlyArray<{
  alias: string;
  token: keyof typeof palette;
  /** The `theme` key the alias used to emit; defaults to `alias`. */
  was?: keyof typeof theme;
}> = [
  { alias: 'colorPrimary', token: 'primary' },
  { alias: 'colorSecondary', token: 'accentFg' },
  { alias: 'colorTertiary', token: 'accentBorder' },
  { alias: 'colorAvatar', token: 'accentBg' },
  { alias: 'colorIcon', token: 'accentFg' },
  { alias: 'colorPrimaryBorderHover', token: 'limeBorder' },
  { alias: 'colorPrimaryBgHover', token: 'limeSoft' },
  { alias: 'colorText', token: 'text' },
  { alias: 'colorTextPrimary', token: 'text' },
  { alias: 'colorTextDescription', token: 'textDescription' },
  { alias: 'colorTextSecondary', token: 'textDescription' },
  { alias: 'colorTextTertiary', token: 'textIcon', was: 'colorTextIcon' },
  { alias: 'colorTextQuaternary', token: 'textPlaceholder', was: 'colorTextPlaceholder' },
  { alias: 'colorTextMuted', token: 'textPlaceholder', was: 'colorTextPlaceholder' },
  { alias: 'colorTextButton', token: 'onPrimary' },
  { alias: 'colorBgContainer', token: 'bgContainer' },
  { alias: 'colorBgElevated', token: 'bgElevated' },
  { alias: 'colorBgDisabled', token: 'surfaceTint3' },
  { alias: 'layoutBg', token: 'bgLayout' },
  { alias: 'colorBorder', token: 'border' },
  { alias: 'colorBorderSecondary', token: 'borderSubtle' },
  { alias: 'colorSplit', token: 'split' },
  { alias: 'colorBgTextHover', token: 'fill2' },
  { alias: 'colorFillSecondary', token: 'fill3' },
  { alias: 'colorFillTertiary', token: 'fill2' },
  { alias: 'colorFillQuaternary', token: 'fill1' },
  { alias: 'colorError', token: 'dangerFg' },
  { alias: 'colorErrorBg', token: 'dangerBg' },
  { alias: 'colorStatusUnknown', token: 'warningFg' },
  { alias: 'colorStatusUnknownBg', token: 'warningBg' },
  { alias: 'colorStatusUnknownBorder', token: 'warningBd' },
  { alias: 'colorBgTag', token: 'accentBgSubtle' },
  { alias: 'colorTagText', token: 'accentFg' },
  { alias: 'colorTagBorder', token: 'accentBorderStrong' },
  { alias: 'colorIconInbox', token: 'textIcon' },
  { alias: 'colorIconWidget', token: 'accentFgStrong' },
  { alias: 'colorCardHover', token: 'surfaceTint3' },
  { alias: 'colorCardShadow', token: 'shadowSm' },
  { alias: 'colorCardShadowHover', token: 'shadowMd' },
  { alias: 'colorAccountTextStrong', token: 'text' },
  { alias: 'colorAccountTextMuted', token: 'textIcon' },
  { alias: 'colorAccountBorderSubtle', token: 'borderSubtle' },
  { alias: 'colorAccountBgElevated', token: 'bgElevated' },
  { alias: 'colorAccountBgPanel', token: 'inputAddon' },
  {
    alias: 'colorNeutralTextDescription',
    token: 'textDescription',
  },
  {
    alias: 'colorNeutralTextPlaceholder',
    token: 'textPlaceholder',
  },
  { alias: 'colorNeutralBorder', token: 'border' },
  { alias: 'colorNeutralSurfaceTint', token: 'surfaceTint1' },
  {
    alias: 'colorNeutralSurfaceTintStrong',
    token: 'surfaceTint3',
  },
  { alias: 'colorHomeBgLayout', token: 'bgLayout' },
  { alias: 'colorHomeBgCard', token: 'bgContainer' },
  { alias: 'colorHomeSplit', token: 'split' },
  { alias: 'colorHomeBorder', token: 'border' },
  { alias: 'colorHomeText', token: 'text' },
  { alias: 'colorHomeTextDescription', token: 'textDescription' },
  { alias: 'colorHomeTextIcon', token: 'textIcon' },
  { alias: 'colorAccentBgSubtle', token: 'accentBgSubtle' },
  { alias: 'colorAccentBg', token: 'accentBg' },
  { alias: 'colorAccentBorder', token: 'accentBorder' },
  { alias: 'colorAccentBorderStrong', token: 'accentBorderStrong' },
  { alias: 'colorAccentFg', token: 'accentFg' },
  { alias: 'colorAccentFgStrong', token: 'accentFgStrong' },
  { alias: 'colorPrimaryBrand', token: 'primary' },
  { alias: 'colorInkStrong', token: 'inkStrong' },
  { alias: 'colorHomeFillContent', token: 'fillContent' },
  { alias: 'colorHomeEmptyPrimary', token: 'accentBgSubtle' },
  {
    alias: 'colorHomeEmptyPrimaryBorder',
    token: 'accentBorder',
  },
  { alias: 'colorHomeEmptyCtaBg', token: 'primary' },
  { alias: 'colorHomeEmptyCtaQuietBg', token: 'fill2' },
  { alias: 'colorHomeEmptyCtaQuietHover', token: 'accentBg' },
  { alias: 'colorHomeEmptyTemplateHover', token: 'accentBg' },
  { alias: 'colorTopBarBg', token: 'accentBgSubtle' },
  { alias: 'colorTopBarBorder', token: 'borderSubtle' },
  { alias: 'colorTopBarText', token: 'text' },
  { alias: 'colorTopBarBadgeBg', token: 'accentBg' },
  { alias: 'colorTopBarBadgeBorder', token: 'accentBorderStrong' },
  { alias: 'colorTopBarBadgeText', token: 'accentFg' },
  { alias: 'colorTopBarReturnHover', token: 'accentFg' },
  { alias: 'colorAssistantBadgeBg', token: 'accentBorder' },
  { alias: 'colorAssistantBadgeText', token: 'accentFg' },
  {
    alias: 'colorAssistantBadgeBorder',
    token: 'accentBorderStrong',
  },
  { alias: 'heroTextColor', token: 'onModulesBackground' },
  { alias: 'colorTitleLogin', token: 'onBackground' },
  { alias: 'colorIconLogin', token: 'onBackground' },
];

/**
 * Emitted on `:root, body`, not on `:root` alone. A `var()` in a custom property
 * is substituted on the element that DECLARES it and children inherit the
 * result, so a `:root`-only alias would stay frozen on Silia's value even after
 * `body.template-*` remaps the `--silia-*` it points at. Declared again on
 * `<body>`, it re-resolves against the brand there.
 *
 * `:root` stays in the selector so `<html>` keeps Silia's values: a page with no
 * brand class, and anything outside `<body>`, still resolve to a real colour.
 */
const legacyColorAliasBlock = `
  :root,
  body {
${LEGACY_COLOR_ALIASES.map(({ alias, token }) => `    --ant-${alias}: ${palette[token]};`).join('\n')}
  }`;

/**
 * The stylesheet injected at app start.
 *
 * Two namespaces are emitted on purpose, for the length of the migration:
 *
 *   `--silia-*`  the real token set. New and migrated code reads only these.
 *   `--ant-*`    aliases for the names `theme-silia-antiguo` already published,
 *                so the CSS that reads them today keeps painting. Ant Design
 *                also generates its own `--ant-*` at runtime; these do not
 *                collide because AntD suffixes its own (`--ant-color-primary`,
 *                camelCase vs kebab). Still — nothing new should be added to
 *                this block. It only shrinks.
 *
 * Delete an alias when the last CSS file reading it has been migrated. When the
 * block is empty, the migration is done.
 */
export const cssVariables = `
  :root {
    /* ── Brand ───────────────────────────────────────────────────────────
       Named --silia-primary rather than plain --silia-lime, because with
       cssVar on AntD
       generates its own preset ramp into this namespace, and one of those
       presets IS called lime. Same reason for --silia-type-size,
       --silia-type-line-height, --silia-ctl-height and --silia-ctl-height-sm
       further down: AntD emits --silia-font-size, --silia-line-height and
       --silia-control-height itself, and crucially it emits them UNITLESS
       (40, not 40px). A collision there is silent — whichever block loses, the
       other side gets "padding: 40" and simply stops applying. Do not
       "restore" these to the obvious name. */
    --silia-ink-strong: ${brand.black};
    --silia-primary: ${brand.lime};
    /* The text and icon colour that sits ON the primary. It is ink on Silia's
       lime and white on a dark primary (PrismHR): a separate token, because
       --silia-ink-strong is also the strongest text on white and cannot be both. */
    --silia-on-primary: ${brand.black};
    --silia-accent-bg-subtle: ${brand.n100};
    --silia-accent-bg: ${brand.n200};
    --silia-accent-border: ${brand.n400};
    --silia-accent-border-strong: ${brand.n500};
    --silia-accent-fg-muted: ${brand.n600};
    --silia-accent-fg: ${brand.n700};
    /* Text of a selected option (the sidebar's System/Favorites toggle). On
       Silia it is the accent ink; a brand can set its own (SL-1920). */
    --silia-selected-fg: ${brand.n700};
    --silia-accent-fg-strong: ${brand.n800};
    --silia-accent-solid: ${brand.n900};

    /* ── Surfaces ──────────────────────────────────────────────────── */
    --silia-bg-layout: ${surface.bgLayout};
    --silia-bg-elevated: ${surface.bgElevated};
    --silia-bg-container: ${surface.bgContainer};
    --silia-surface-tint-1: ${surface.tint1};
    --silia-surface-tint-2: ${surface.tint2};
    --silia-surface-tint-3: ${surface.tint3};
    --silia-input-addon: ${surface.inputAddon};

    /* ── Text & icons ──────────────────────────────────────────────── */
    --silia-text: ${ink.text};
    --silia-text-description: ${ink.textDescription};
    --silia-text-icon: ${ink.textIcon};
    --silia-text-placeholder: ${ink.textPlaceholder};
    --silia-text-inverse: ${ink.textInverse};

    /* ── Borders ───────────────────────────────────────────────────── */
    --silia-border: ${line.border};
    --silia-border-subtle: ${line.borderSubtle};
    --silia-fill-content: ${line.fillContent};
    --silia-split: ${line.split};

    /* ── Semantic feedback ─────────────────────────────────────────── */
    --silia-success-bg: ${semantic.successBg};
    --silia-success-fg: ${semantic.successFg};
    --silia-success-fg-strong: ${semantic.successFgStrong};
    --silia-success-bd: ${semantic.successBd};
    --silia-warning-bg: ${semantic.warningBg};
    --silia-warning-fg: ${semantic.warningFg};
    --silia-warning-fg-strong: ${semantic.warningFgStrong};
    --silia-warning-bd: ${semantic.warningBd};
    --silia-danger-bg: ${semantic.dangerBg};
    --silia-danger-fg: ${semantic.dangerFg};
    --silia-danger-fg-strong: ${semantic.dangerFgStrong};
    --silia-danger-bd: ${semantic.dangerBd};
    --silia-info-bg: ${semantic.infoBg};
    --silia-info-fg: ${semantic.infoFg};
    --silia-info-fg-strong: ${semantic.infoFgStrong};
    --silia-info-bd: ${semantic.infoBd};

    /* ── Tag palette extras ────────────────────────────────────────── */
    --silia-tag-red-fg: ${tagExtras.redFg};
    --silia-tag-blue-fg: ${tagExtras.blueFg};
    --silia-tag-amber-hue: ${tagExtras.amberHue};
    --silia-tag-cyan-bg: ${tagExtras.cyanBg};
    --silia-tag-cyan-fg: ${tagExtras.cyanFg};
    --silia-tag-cyan-hue: ${tagExtras.cyanHue};
    --silia-tag-orange-bg: ${tagExtras.orangeBg};
    --silia-tag-orange-fg: ${tagExtras.orangeFg};
    --silia-tag-orange-hue: ${tagExtras.orangeHue};
    --silia-tag-pink-bg: ${tagExtras.pinkBg};
    --silia-tag-pink-fg: ${tagExtras.pinkFg};
    --silia-tag-pink-hue: ${tagExtras.pinkHue};
    --silia-tag-brown-bg: ${tagExtras.brownBg};
    --silia-tag-brown-fg: ${tagExtras.brownFg};
    --silia-tag-brown-hue: ${tagExtras.brownHue};

    /* ── Tag palette weights (fill / tag / dot / text) ──────────────── */
${tagVars}

    /* ── Emotion avatars ───────────────────────────────────────────── */
    --silia-emotion-positive-bg: ${emotion.positiveBg};
    --silia-emotion-positive-fg: ${emotion.positiveFg};
    --silia-emotion-neutral-bg: ${emotion.neutralBg};
    --silia-emotion-neutral-fg: ${emotion.neutralFg};
    --silia-emotion-negative-bg: ${emotion.negativeBg};
    --silia-emotion-negative-fg: ${emotion.negativeFg};

    /* ── Object kind ───────────────────────────────────────────────────
       Declared as literals, not as var() aliases. A var() alias resolves in
       the element where it is DECLARED, so an alias written only in :root
       would stay frozen on Silia's purple even after a template remaps it —
       which is why the prototype redeclares these inside every template. */
    --silia-kind-folder-bg: ${kind.folderBg};
    --silia-kind-folder-fg: ${kind.folderFg};
    --silia-kind-folder-bd: ${kind.folderBd};
    --silia-kind-agent-bg: ${kind.agentBg};
    --silia-kind-agent-fg: ${kind.agentFg};
    --silia-kind-agent-bd: ${kind.agentBd};
    --silia-kind-agent-soft-bg: ${kind.agentSoftBg};
    --silia-kind-agent-soft-fg: ${kind.agentSoftFg};
    --silia-kind-agent-soft-bd: ${kind.agentSoftBd};
    --silia-kind-qa-bg: ${kind.qaBg};
    --silia-kind-qa-fg: ${kind.qaFg};
    --silia-kind-qa-bd: ${kind.qaBd};
    --silia-kind-rta-bg: ${kind.rtaBg};
    --silia-kind-rta-fg: ${kind.rtaFg};
    --silia-kind-rta-bd: ${kind.rtaBd};
    --silia-kind-data-view-bg: ${kind.dataViewBg};
    --silia-kind-data-view-fg: ${kind.dataViewFg};
    --silia-kind-data-view-bd: ${kind.dataViewBd};
    --silia-kind-dashboard-bg: ${kind.dashboardBg};
    --silia-kind-dashboard-fg: ${kind.dashboardFg};
    --silia-kind-dashboard-bd: ${kind.dashboardBd};
    --silia-kind-file-bg: ${kind.fileBg};
    --silia-kind-file-fg: ${kind.fileFg};
    --silia-kind-file-bd: ${kind.fileBd};

    /* ── Canvas node type ──────────────────────────────────────────── */
    --silia-node-tool-bg: ${node.toolBg};
    --silia-node-tool-fg: ${node.toolFg};
    --silia-node-tool-bd: ${node.toolBd};
    --silia-node-mcp-bg: ${node.mcpBg};
    --silia-node-mcp-fg: ${node.mcpFg};
    --silia-node-mcp-bd: ${node.mcpBd};
    --silia-node-table-bg: ${node.tableBg};
    --silia-node-table-fg: ${node.tableFg};
    --silia-node-table-bd: ${node.tableBd};
    --silia-node-agent-bg: ${node.agentBg};
    --silia-node-agent-fg: ${node.agentFg};
    --silia-node-agent-bd: ${node.agentBd};

    /* ── Translucent fills & scrim ─────────────────────────────────── */
    --silia-fill-1: ${fill.f1};
    --silia-fill-2: ${fill.f2};
    --silia-fill-3: ${fill.f3};
    --silia-fill-brand-1: ${fill.brand1};
    --silia-fill-brand-2: ${fill.brand2};
    --silia-fill-inverse-1: ${fill.inverse1};
    --silia-fill-inverse-2: ${fill.inverse2};
    --silia-scrim: ${fill.scrim};
    --silia-scrim-strong: ${fill.scrimStrong};
    --silia-primary-bg-subtle: ${fill.limeSoft};
    --silia-primary-border: ${fill.limeBorder};
    --silia-primary-bg: ${fill.limeTenue};

    /* ── Elevation ─────────────────────────────────────────────────── */
    --silia-shadow-sm: ${elevation.sm};
    --silia-shadow-md: ${elevation.md};
    --silia-shadow-lg: ${elevation.lg};
    --silia-shadow-xl: ${elevation.xl};
    --silia-focus-ring: ${elevation.focusRing};
    --silia-shadow-glow: ${elevation.glow};
    --silia-shadow-cta: ${elevation.cta};
    --silia-shadow-cta-hover: ${elevation.ctaHover};

    /* ── Typography ────────────────────────────────────────────────── */
    --silia-font-sans: ${typography.fontSans};
    --silia-font-mono: ${typography.fontMono};
    --silia-type-size: ${typography.fontSize}px;
    --silia-font-size-icons: ${typography.fontSizeIcons}px;
    --silia-type-line-height: ${typography.lineHeight};

    /* ── Shape ─────────────────────────────────────────────────────── */
    --silia-radius-xs: ${radius.xs}px;
    --silia-radius-sm: ${radius.sm}px;
    --silia-radius-md: ${radius.md}px;
    --silia-radius-lg: ${radius.lg}px;
    --silia-radius-pill: ${radius.pill}px;
    --silia-radius-circle: ${radius.circle};

    /* ── Space ─────────────────────────────────────────────────────── */
    --silia-space-xxs: ${space.xxs}px;
    --silia-space-xs: ${space.xs}px;
    --silia-space-sm: ${space.sm}px;
    --silia-space-md: ${space.md}px;
    --silia-space-lg: ${space.lg}px;
    --silia-space-xl: ${space.xl}px;
    --silia-space-xxl: ${space.xxl}px;

    /* ── Controls ──────────────────────────────────────────────────── */
    --silia-ctl-height: ${control.height}px;
    --silia-ctl-height-sm: ${control.heightSM}px;

    /* ── Page shell ────────────────────────────────────────────────────
       The chrome every listing screen repeats: a padded page, a breadcrumb,
       and one white card holding the title, the toolbar and the content.

       Twelve screens had built it by hand — org-page, skills-page__card,
       wizard__shell, overview-shell__card, users-card, details-card,
       assistant-management-card, metrics-header-card and four bare AntD
       Cards — between them using six card paddings, five title sizes and
       four breadcrumb gaps. These are the numbers Organizations settled on,
       which are the ones already on the scale.

       The responsive steps are here too: AntD has no breakpoint token, so a
       stylesheet still writes the media query, but it reads the value from
       here instead of inventing 20px and 10px, neither of which is on any
       scale. */
    --silia-page-padding: ${space.lg}px;
    --silia-page-padding-tablet: ${space.md}px;
    --silia-page-padding-mobile: ${space.sm}px;
    --silia-page-gap: ${space.md}px;
    --silia-page-card-padding: ${space.lg}px;
    --silia-page-card-padding-tablet: ${space.md}px;
    --silia-page-card-gap: ${space.lg}px;
    --silia-page-card-gap-tablet: ${space.md}px;

    /* ── Headings ──────────────────────────────────────────────────────
       AntD emits its own --silia-font-size-heading-N, but scoped to the
       .silia class it puts on its components — a stylesheet reading them
       from a plain element resolves nothing, silently. These are the same
       numbers under a name of ours, safe to read anywhere.

       --silia-title-* rather than --silia-heading-*: AntD also generates
       --silia-font-size-heading-N and --silia-line-height-heading-N, and a
       collision in that namespace is invisible. */
    --silia-title-1: ${typography.fontSizeHeading1}px;
    --silia-title-2: ${typography.fontSizeHeading2}px;
    --silia-title-3: ${typography.fontSizeHeading3}px;
    --silia-title-4: ${typography.fontSizeHeading4}px;
    --silia-title-5: ${typography.fontSizeHeading5}px;
    --silia-title-1-lh: ${Math.round(typography.fontSizeHeading1 * typography.lineHeightHeading1)}px;
    --silia-title-2-lh: ${Math.round(typography.fontSizeHeading2 * typography.lineHeightHeading2)}px;
    --silia-title-3-lh: ${Math.round(typography.fontSizeHeading3 * typography.lineHeightHeading3)}px;
    --silia-title-4-lh: ${Math.round(typography.fontSizeHeading4 * typography.lineHeightHeading4)}px;
    --silia-title-5-lh: ${Math.round(typography.fontSizeHeading5 * typography.lineHeightHeading5)}px;

    /* ── Home welcome banner artwork ────────────────────────────────────
       An illustration, not a ramp step: five radial gradients over a linear
       one. It still belongs to the brand — PrismHR's Home must not wear Silia's
       violet and lime — so each brand declares its own (SL-1918). */
    --silia-welcome-artwork: ${theme.colorHomeBannerGradient};
    --silia-welcome-text-shadow: ${theme.colorHomeBannerTextShadow};

    /* ── Brand assets ──────────────────────────────────────────────────
       The same list every brand block emits (brands/assets.ts), so :root and
       the brands cannot drift apart. */
${emitAssetVars(siliaAssets)}

    /* ── Text over the brand's background images ────────────────────────
       Silia's are creams on dark photos; a brand with light images declares a
       dark one (SL-1920). Read through --ant-colorTitleLogin /
       --ant-colorIconLogin / --ant-heroTextColor (LEGACY_COLOR_ALIASES). */
    --silia-on-background: ${theme.colorTitleLogin};
    --silia-on-modules-background: ${theme.heroTextColor};

    /* ═══════════════════════════════════════════════════════════════════
       LEGACY --ant-* ALIASES — migration scaffolding, delete as you go.
       Every name below existed in theme-silia-antiguo. Nothing new goes here.
       The COLOUR aliases are not here: they live in LEGACY_COLOR_ALIASES and
       are emitted on \`:root, body\` so they follow the brand (SL-1918). What
       stays is metric only; assets come from emitAssetVars above, and the
       login colours resolve through --silia-on-background (SL-1920).
       ═══════════════════════════════════════════════════════════════════ */
${tagVarsLegacy}

    --ant-fontSizeIcons: ${theme.fontSizeIcons}px;
    --ant-borderRadiusXS: ${theme.borderRadiusXS}px;
    --ant-borderRadiusSM: ${theme.borderRadiusSM}px;
    --ant-paddingXXL: ${theme.paddingXXL}px;
    --ant-paddingXL: ${theme.paddingXL}px;
    --ant-paddingLG: ${theme.paddingLG}px;
    --ant-paddingSM: ${theme.paddingSM}px;
    --ant-paddingXS: ${theme.paddingXS}px;
    --ant-marginXL: ${theme.marginXL}px;
    --ant-marginLG: ${theme.marginLG}px;
    --ant-marginSM: ${theme.marginSM}px;
    --ant-marginXS: ${theme.marginXS}px;
    --ant-gapXL: ${theme.gapXL}px;
    --ant-gapLG: ${theme.gapLG}px;
    --ant-gapSM: ${theme.gapSM}px;
    --ant-gapXS: ${theme.gapXS}px;
  }
${legacyColorAliasBlock}

  .ant-typography,
  .ant-typography h1,
  .ant-typography h2,
  .ant-typography h3,
  .ant-typography h4,
  .ant-typography h5,
  .ant-typography h6,
  .ant-typography p {
    margin: 0 !important;
  }

  /* The dropdown shell carries a border.

     AntD gives the popup a shadow but no outline, so on the elevated off-white
     it floats without an edge — against a card of a similar tone the boundary
     reads as soft rather than as a separate surface. The border closes the
     rectangle. It is a rule because the shell exposes no border token; the
     radius and background are tokens (Dropdown.borderRadiusLG /
     colorBgElevated) and stay there.

     Padding on the menu itself, so the first and last rows are not flush
     against the border. */
  .ant-dropdown-menu {
    border: 1px solid var(--silia-border);
  }

  /* The row's height is a floor with the content centred in it, which is how the
     account-table menu is built. Setting it as vertical padding instead makes
     the row grow when a label wraps; a floor keeps every row the same height and
     lets a long one grow only when it has to.

     The horizontal inset is controlPaddingHorizontal, which is the token AntD
     writes into the row's own padding. Margin is not an option here: AntD
     declares margin:0 on this same selector at three classes, so a rule of
     two loses — an earlier attempt at margin-inline never applied at all. */
  .ant-dropdown-menu-item,
  .ant-dropdown-menu-submenu-title {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  /* The destructive row fills on hover.

     AntD has tokens for the danger colour at rest and on hover, but the only
     one that paints a BACKGROUND is dangerItemActiveBg, which it applies on
     :active — the click, not the hover. So the fill has to be a rule.

     It has to outrank AntD's own, and AntD does not play small: its generated
     selector carries ten classes and two :not() pseudo-classes
     (.ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger
     plus two :not() clauses),
     and its stylesheet is injected after this one. A two-class rule here loses
     silently — it does not even show as overridden in devtools, it just never
     applies. Hence !important, which is the one thing that clears a
     ten-class selector without racing it on count.

     The label and the icon are separate elements painting with their own
     colour, and the icon's glyph is an SVG filling from currentColor, so all
     of them have to be named or the row turns red with red text on it.

     The fill is the DARK red step. White on #ff4d4f measures 3.27:1, under the
     4.5 floor; on #cf1322 it is 5.57:1. And it has to be filled at all — white
     on the menu's ordinary hover grey (#eeeeec) is 1.16:1, an invisible label.

     This replaced the same block hand-written in three stylesheets, one of which
     (KnowledgeList.css) was BOTH unscoped and wrong: it forced the danger colour
     to stay red on hover, app-wide, which painted red text on the red fill. */
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover,
  .ant-dropdown-menu-submenu .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
    background-color: var(--silia-danger-fg-strong) !important;
  }

  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-title-content,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-item-icon,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-item-icon svg,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover .anticon,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover .anticon svg {
    color: var(--silia-text-inverse) !important;
    fill: currentColor !important;
  }

  /* The tooltip bubble centres its label.

     AntD gives the inner box a \`minHeight\` but leaves it a plain block, so
     whenever the floor is taller than the text — a short tip, or one whose
     content is shorter than the line box — the slack collects at the bottom and
     the label rides high. Scoping \`controlHeight\` to 32 (see the Tooltip
     entry) fixes the common one-line case by making the content taller than the
     floor; this makes it hold for every case, including two-line tips and the
     ones that carry an icon. */
  .ant-tooltip-inner {
    display: flex;
    align-items: center;
  }

  /* The Segmented row height. It lives here rather than in the component token
     block because for Segmented \`controlHeight\` is an alias token — set inside
     \`components.Segmented\` it resolves to the global 40 and the row stays tall.
     32 is the toolbar height, which is what a view switch beside a title wants.

     This one rule replaced the same block hand-written in nine stylesheets, in
     four different mechanisms (CSS, a nested ConfigProvider, inline \`style\`,
     and a "canonical" wrapper duplicated across two features), with four
     different track greys between them. */
  .ant-segmented .ant-segmented-item-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    line-height: 32px;
  }

  /* size="large" is the opt-out, for the few rows that are a form control
     rather than a view switch. */
  .ant-segmented-lg .ant-segmented-item-label {
    min-height: 40px;
    line-height: 40px;
  }

  /* AntD paints its \`Tag\` text with the semantic FILL colour — a value meant
     for an icon, not for 9-12px text on the soft background. Measured, that
     lands between 2.47:1 and 3.66:1. These push each one to its dark step,
     which is the same rule the data palette follows. */
  .ant-tag-success { color: var(--silia-success-fg-strong); }
  .ant-tag-error { color: var(--silia-danger-fg-strong); }
  .ant-tag-warning { color: var(--silia-warning-fg-strong); }
  .ant-tag-processing { color: var(--silia-info-fg-strong); }
  .ant-tag-default,
  .ant-tag:not([class*='ant-tag-']) { color: var(--silia-text-description); }
`;

/**
 * El stylesheet con los alias de rol del paso 04 (fase 1) agregados.
 *
 * Los dos nombres resuelven al mismo valor, así que durante la fase 2 no existe
 * la ventana en la que romper sea posible. Ver `roleAliases.ts`.
 */
